import { NgClass, NgTemplateOutlet } from '@angular/common'
import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnInit,
  Output,
  signal,
  TrackByFunction,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NavigationSkipped, NavigationStart, Router, RouterModule } from '@angular/router'
import { CourtSystemWithMetadata } from '@ftr/contracts/api/court-system'
import { Uuid } from '@ftr/contracts/type/shared'
import {
  BrowserSnifferService,
  ButtonComponent,
  FocusableNoopAnchorDirective,
  IconLegacyComponent,
  TooltipModule,
} from '@ftr/foundation'
import { HelpModule } from '@ftr/ui-help'
import { AuthenticationService, GetCurrentUserGroupsAction } from '@ftr/ui-user'
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngxs/store'
import {
  BranchNavItem,
  COURT_SYSTEM_NAV_ITEMS,
  HELP_CENTER_NAV_TITLE,
  SideNavDisplayMode,
  SideNavGroup,
  SideNavItem,
} from '../../navigation/side-nav-item'
import { SideNavProviderType } from '../../navigation/side-nav-items.provider'
import { CourtSystemDropdownComponent } from '../court-system-dropdown/court-system-dropdown.component'
import { FavoritesEditModalComponent } from '../favorites/favorites-edit-modal/favorites-edit-modal.component'
import { FavoritesMenuComponent } from '../favorites/favorites-menu/favorites-menu.component'

@Component({
  selector: 'ftr-side-nav-display',
  templateUrl: './side-nav-display.component.html',
  styleUrls: ['./side-nav-display.component.css'],
  imports: [
    IconLegacyComponent,
    ButtonComponent,
    NgClass,
    TooltipModule,
    NgTemplateOutlet,
    NgbPopoverModule,
    HelpModule,
    CourtSystemDropdownComponent,
    FavoritesMenuComponent,
    RouterModule,
    FavoritesEditModalComponent,
    FocusableNoopAnchorDirective,
  ],
})
export class SideNavDisplayComponent implements OnInit {
  @Input() displayMode: SideNavDisplayMode = 'DEFAULT'
  @Input() expanded: boolean = true
  @Input() isLoading: boolean = false
  @Input() currentCourtSystemId: Uuid | undefined
  @Input() isOpened: boolean
  @Output() sideBarToggled = new EventEmitter<void>()
  @Output() expandedToggled = new EventEmitter<void>()

  navGroups = input([] as SideNavGroup[])
  footerItems = input([] as SideNavItem[])
  providerType = input<SideNavProviderType | undefined>(undefined)
  nestedNavItems = signal([] as BranchNavItem[])
  selectedNestedItem = computed(() =>
    this.nestedNavItems().length > 0 ? this.nestedNavItems()[this.nestedNavItems().length - 1] : undefined,
  )

  loadingWidths: string[] = Array.from({ length: 25 }, () => `${Math.floor(Math.random() * (96 - 55)) + 55}%`)
  loadingSideNavGroups: SideNavGroup[] = Array.from({ length: 4 }, () => ({
    isLoading: true,
  }))

  protected isExpandButtonHovered = signal(false)

  readonly helpCenterNavTitle = HELP_CENTER_NAV_TITLE

  readonly isFirefox = computed(() => this.browserSnifferService.isFirefox())

  readonly #destroyRef = inject(DestroyRef)

  constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly browserSnifferService: BrowserSnifferService,
    private readonly authenticationService: AuthenticationService,
  ) {}

  async ngOnInit(): Promise<void> {
    // To ensure court systems and permissions are in the state
    // before we check them in the internal and external side nav providers.
    if (await this.authenticationService.isLoggedIn) {
      this.store.dispatch(new GetCurrentUserGroupsAction())
    }
    this.watchRouterEvents()
  }

  popNestedNavItems(): void {
    this.nestedNavItems.update(previousNavItems => {
      return previousNavItems.slice(0, previousNavItems.length - 1)
    })
  }

  handleOnNavItemClick(sideNavItem: SideNavItem): void {
    if (sideNavItem.type === 'BRANCH') {
      this.nestedNavItems.update(previousNavItems => {
        return [...previousNavItems, sideNavItem]
      })
    } else if (sideNavItem.type === 'BUTTON') {
      if (sideNavItem.onSelect) {
        sideNavItem.onSelect()
      }
      this.closeSideBar()
    }
  }

  closeSideBar(): void {
    this.nestedNavItems.update(() => [])
    this.sideBarToggled.emit()
  }

  onCourtSystemDropdownOpened(courtSystem?: CourtSystemWithMetadata): void {
    if (courtSystem) {
      this.handleOnNavItemClick({
        type: 'BRANCH',
        title: courtSystem?.name,
        children: COURT_SYSTEM_NAV_ITEMS,
      } as SideNavItem)
    } else {
      this.handleOnNavItemClick({
        type: 'BRANCH',
        title: 'For The Record',
        children: COURT_SYSTEM_NAV_ITEMS.slice(0, 1),
      } as SideNavItem)
    }
  }

  onFavoritesDropdownOpened(actions: SideNavItem[]): void {
    this.handleOnNavItemClick({
      type: 'BRANCH',
      title: 'Favorites',
      children: actions,
    })
  }

  trackBy: TrackByFunction<SideNavGroup | SideNavItem> = (index: number) => {
    return index
  }

  skipToMainContent(): void {
    const mainContent = document.getElementById('main-content')
    mainContent?.focus()
  }

  private watchRouterEvents(): void {
    // Close the side nav if we try to navigate away from the current page
    this.router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(event => {
      if (this.displayMode === 'OVERLAY' && this.isOpened) {
        if (event instanceof NavigationStart || event instanceof NavigationSkipped) {
          this.closeSideBar()
        }
      }
    })
  }
}
